import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import { appInfo } from '@/lib/appInfo';
import { Link } from 'react-router-dom';

interface AboutPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function AboutPanel({ open, onClose }: AboutPanelProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const text = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const muted = isDark ? 'text-[#A1A1A6]' : 'text-neutral-500';
  const card = isDark ? 'border-white/[0.05] bg-white/[0.03]' : 'border-neutral-100 bg-neutral-50';
  return (
    <AnimatePresence>
      {open && <div className="fixed inset-0 z-[100] flex items-end lg:items-center lg:justify-end">
        <motion.div className={`absolute inset-0 ${isDark ? 'bg-black/60' : 'bg-black/30'}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
        <motion.div className={`relative w-full lg:w-[400px] lg:h-full ${isDark ? 'bg-[#161618] border-white/[0.05]' : 'bg-white border-neutral-200'} lg:border-l overflow-hidden flex flex-col`}
          initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 320, damping: 30 }}
          style={{ maxHeight: '85vh', borderRadius: '24px 24px 0 0' }}>
          <div className="flex justify-center pt-3 pb-1 lg:hidden"><div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-black/20'}`} /></div>
          <div className="flex items-center justify-between px-5 pt-2 pb-3">
            <h3 className={`text-base font-semibold ${text}`}>About LAVYN</h3>
            <Pressable onClick={onClose} aria-label="Close About LAVYN" scale={0.88}
              className={`w-9 h-9 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'} ${muted}`}>
              <i className="ri-close-line" />
            </Pressable>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-8">
            <div className="text-center py-6">
              <h2 className={`text-xl font-bold ${text}`}>LAVYN</h2>
              <p className={`text-sm mt-2 ${muted}`}>Your AI Study Tutor</p>
              <p className={`text-xs mt-2 ${muted}`}>Version {appInfo.version}</p>
            </div>
            <div className={`rounded-2xl border overflow-hidden ${card}`}>
              <Link to="/about" onClick={onClose} className={`block px-4 py-3.5 text-sm font-medium underline underline-offset-4 ${text}`}>About LAVYN and study tools</Link>
              <Link to="/contact" onClick={onClose} className={`block px-4 py-3.5 text-sm font-medium underline underline-offset-4 ${text}`}>Help &amp; Support</Link>
              {[
                { label: 'Terms of Service', url: appInfo.termsUrl },
                { label: 'Privacy Policy', url: appInfo.privacyUrl },
              ].map(({ label, url }) => <div key={label} className={`px-4 py-3.5 ${text}`}>
                {url ? <Link to={url} onClick={onClose} className="block text-sm font-medium underline underline-offset-4">{label}</Link>
                  : <><span className="text-sm font-medium">{label}</span><p className={`text-xs mt-1 ${muted}`}>Not published yet.</p></>}
              </div>)}
            </div>
          </div>
        </motion.div>
      </div>}
    </AnimatePresence>
  );
}
